import { Leaf, Shield, Heart } from "lucide-react";
import heroImg from "../assets/hero-bg.jpg";
import { Link } from "react-router-dom";
import WhatsAppButton from "../components/WhatsAppButton";
import ProductCard from "../components/ProductCard";
import { useEffect, useState } from "react";
import { fetchFeaturedProducts } from "../services/productApi";
import { useTranslation } from "react-i18next";

const Index = () => {
   const { t } = useTranslation();

  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFeaturedProducts = async () => {
      try {
        const data = await fetchFeaturedProducts();
        setFeaturedProducts(data);
      } catch (err) {
        setError("Failed to load products");
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedProducts();
  }, []);

  const benefits = [
    {
      icon: Leaf,
      title: t("home.benefits.purity.title"),
      description: t("home.benefits.purity.description"),
    },
    {
      icon: Shield,
      title:t("home.benefits.sustainability.title"),
      description: t("home.benefits.sustainability.description"),
    },
    {
      icon: Heart,
      title: t("home.benefits.care.title"),
      description: t("home.benefits.care.description"),
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt="Fresh herbs arrangement"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 overlay-green" />
        </div>

        {/* Content */}
        <div className="relative z-10 container-custom text-center text-cream px-4">
          <div className="max-w-3xl mx-auto space-y-8">
            <h1
              className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold leading-tight animate-fade-in"
              style={{ animationDelay: "0.2s" }}
            >
               {t("home.hero.title")}{" "}
               <br />
              <span className="text-sage">{t("home.hero.highlight")}</span>
            </h1>

            <p
              className="text-lg md:text-xl text-cream/90 max-w-2xl mx-auto animate-fade-in"
              style={{ animationDelay: "0.4s" }}
            >
             {t("home.hero.subtitle")}
            </p>

            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in"
              style={{ animationDelay: "0.6s" }}
            >
              <WhatsAppButton variant="hero" />
              <Link
                to="/products"
                className="btn-hero bg-cream/10 border-2 border-cream hover:bg-cream hover:text-primary"
              >
              {t("home.hero.explore")}
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
          <div className="w-6 h-10 border-2 border-cream/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-cream/70 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-secondary">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
             {t("home.benefits.title")}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("home.benefits.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className="benefit-card animate-slide-up"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-primary/10 rounded-2xl flex items-center justify-center">
                  <benefit.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-foreground mb-3">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
              {t("home.featured.title")}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
             {t("home.featured.subtitle")}
            </p>
          </div>

          {loading && <p className="text-center">{t("home.loadingProducts")}</p>}
          {error && <p className="text-center text-red-500">{error}</p>}

          {!loading && !error && featuredProducts.length === 0 && (
            <div className="text-center py-16">
               <Leaf className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg text-muted-foreground">
                 {t("home.noProducts")}
              </p>
            </div>
          )}

          {!loading && !error && featuredProducts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {featuredProducts.map((product, index) => (
                <div
                  key={product._id || product.id}
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/products" className="btn-hero inline-flex">
             {t("home.viewAll")}
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
             {t("home.cta.title")}
          </h2>
          <p className="text-primary-foreground/80 max-w-2xl mx-auto mb-8">
            {t("home.cta.subtitle")}
          </p>
          <WhatsAppButton variant="hero" />
        </div>
      </section>
    </div>
  );
};

export default Index;
